import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, ChevronRight, Check, X, AlertTriangle } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { ClavePlantilla, ESTADO_PLANTILLA, ModeloPlantilla, NOMBRE_MODELO, Preajuste } from '@/lib/contratos';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

function resumenPreajuste(p: Preajuste): string {
  const partes: string[] = [];
  const valores = Object.entries(p.valores || {});
  if (valores.length) partes.push(valores.map(([k, v]) => `${k} = ${v}`).join(', '));
  const variantes = Object.keys(p.variantes || {}).length;
  if (variantes) partes.push(`${variantes} variante${variantes > 1 ? 's' : ''}`);
  const retoques = Object.keys(p.sobrescrituras || {}).length;
  if (retoques) partes.push(`${retoques} cláusula${retoques > 1 ? 's' : ''} retocada${retoques > 1 ? 's' : ''}`);
  const extras = (p.clausulasExtra || []).length;
  if (extras) partes.push(`${extras} añadida${extras > 1 ? 's' : ''}`);
  return partes.join(' · ') || 'Sin cambios sobre la plantilla';
}

export default function PlantillasPage() {
  const navigate = useNavigate();
  const { data: modelos, loading, refresh } = useApi<ModeloPlantilla[]>(() => api.getPlantillasContrato(), []);
  const { data: preajustes, refresh: refreshPreajustes } = useApi<Preajuste[]>(() => api.getPreajustesContrato(), []);
  const [creando, setCreando] = useState<ClavePlantilla | null>(null);
  const [editando, setEditando] = useState<{ id: string; nombre: string; descripcion: string } | null>(null);
  const [guardando, setGuardando] = useState(false);

  const nuevoBorrador = async (key: ClavePlantilla) => {
    setCreando(key);
    try {
      const v = await api.crearBorradorPlantilla(key);
      navigate(`/contracts/plantillas/${key}/${v.version}`);
    } catch (err: any) {
      alert(err.message || 'No se pudo crear el borrador');
    } finally {
      setCreando(null);
    }
  };

  const guardarPreajuste = async () => {
    if (!editando) return;
    setGuardando(true);
    try {
      await api.actualizarPreajuste(editando.id, { nombre: editando.nombre.trim(), descripcion: editando.descripcion.trim() });
      setEditando(null);
      refreshPreajustes();
    } catch (err: any) {
      alert(err.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const marcarRevisado = async (p: Preajuste) => {
    try {
      await api.marcarPreajusteRevisado(p.id);
      refreshPreajustes();
    } catch (err: any) {
      alert(err.message || 'No se pudo marcar');
    }
  };

  const eliminarPreajuste = async (p: Preajuste) => {
    if (!confirm(`¿Eliminar el preajuste «${p.nombre}»? Los contratos que lo usaron no cambian.`)) return;
    try {
      await api.eliminarPreajuste(p.id);
      refreshPreajustes();
    } catch (err: any) {
      alert(err.message || 'No se pudo eliminar');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-8">
      <section className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {(modelos || []).map(m => {
          const hayBorrador = m.versiones.some(v => v.estado === 'borrador');
          return (
            <div key={m.templateKey} className="card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold">{NOMBRE_MODELO[m.templateKey]}</h2>
                  <p className="text-sm text-sereno-dark/50">
                    {m.versionPublicada ? `Versión ${m.versionPublicada} publicada` : 'Sin versión publicada'}
                  </p>
                </div>
                <button
                  onClick={() => nuevoBorrador(m.templateKey)}
                  disabled={creando === m.templateKey || hayBorrador}
                  className="btn-secondary text-sm flex items-center gap-1.5"
                  title={hayBorrador ? 'Ya hay un borrador abierto' : 'Abrir una versión nueva para editarla'}
                >
                  {creando === m.templateKey ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Nueva versión
                </button>
              </div>
              <ul className="divide-y divide-gray-100">
                {[...m.versiones].reverse().map(v => (
                  <li key={v.id}>
                    <Link to={`/contracts/plantillas/${v.templateKey}/${v.version}`} className="flex items-center gap-3 py-2.5 hover:bg-sereno-blue/10 -mx-2 px-2 rounded-lg">
                      <span className="font-mono text-xs font-semibold w-8">v{v.version}</span>
                      <span className={`badge ${ESTADO_PLANTILLA[v.estado].className}`}>{ESTADO_PLANTILLA[v.estado].label}</span>
                      <span className="text-sm text-sereno-dark/60 flex-1 truncate">{v.clausulas} cláusulas · {v.notas || 'sin notas'}</span>
                      <span className="text-xs text-sereno-dark/40 whitespace-nowrap">{formatDateTime(v.publicadaAt || v.updatedAt || v.createdAt)}</span>
                      <ChevronRight size={14} className="text-sereno-dark/30" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <section className="card">
        <div className="mb-3">
          <h2 className="text-lg font-semibold">Preajustes</h2>
          <p className="text-sm text-sereno-dark/50">
            Condiciones ya negociadas con nombre. Se crean desde un contrato («Guardar estas condiciones como preajuste») y se eligen al generar el siguiente.
          </p>
        </div>
        {(preajustes || []).length === 0 ? (
          <p className="text-sm text-sereno-dark/40">Todavía no hay preajustes.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {(preajustes || []).map(p => (
              <li key={p.id} className="py-3 flex items-start gap-3">
                <span className="badge bg-sereno-blue text-sereno-dark whitespace-nowrap mt-0.5">{NOMBRE_MODELO[p.templateKey]}</span>
                <div className="flex-1 min-w-0">
                  {editando?.id === p.id ? (
                    <div className="space-y-2">
                      <input className="input" value={editando.nombre} onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
                      <input className="input" placeholder="Descripción" value={editando.descripcion} onChange={e => setEditando({ ...editando, descripcion: e.target.value })} />
                      <div className="flex gap-2">
                        <button onClick={guardarPreajuste} disabled={guardando || !editando.nombre.trim()} className="btn-primary text-xs py-1 flex items-center gap-1"><Check size={12} /> Guardar</button>
                        <button onClick={() => setEditando(null)} className="btn-ghost text-xs py-1 flex items-center gap-1"><X size={12} /> Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium">
                        {p.nombre} <span className="text-xs text-sereno-dark/40 font-normal">· plantilla v{p.templateVersion}</span>
                        {p.revisar && <span className="badge bg-amber-100 text-amber-800 ml-2">Revisar</span>}
                      </p>
                      {p.descripcion && <p className="text-sm text-sereno-dark/60">{p.descripcion}</p>}
                      <p className="text-xs text-sereno-dark/40 mt-0.5 font-mono">{resumenPreajuste(p)}</p>
                      {p.revisar && (
                        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-900">
                          <p className="font-medium flex items-center gap-1"><AlertTriangle size={12} /> La plantilla pasó de la v{p.revisar.desdeVersion} a la v{p.revisar.hastaVersion} y cambió algo que este preajuste toca:</p>
                          <ul className="list-disc pl-4 mt-1 space-y-0.5">{p.revisar.motivos.map((m, i) => <li key={i}>{m}</li>)}</ul>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Link to={`/contracts/plantillas/${p.templateKey}/${p.revisar.hastaVersion}`} className="underline">Ver la versión nueva</Link>
                            <button onClick={() => marcarRevisado(p)} className="underline">Ya lo he revisado</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {editando?.id !== p.id && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditando({ id: p.id, nombre: p.nombre, descripcion: p.descripcion || '' })} className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/40 hover:text-sereno-dark" title="Renombrar"><Pencil size={14} /></button>
                    <button onClick={() => eliminarPreajuste(p)} className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/40 hover:text-red-600" title="Eliminar"><Trash2 size={14} /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-sereno-dark/40 mt-3">
          Al publicar una versión nueva de la plantilla, los preajustes que tocan cláusulas que han cambiado quedan marcados aquí con «Revisar» y el motivo. {' '}
          <button onClick={refresh} className="underline">Actualizar</button>
        </p>
      </section>
    </div>
  );
}
